import { useEffect, useState } from "react";

import { IconButton } from "@similarweb/ui-components";
import { Box, Flex, Stack } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import useFeedbackMutation from "app/pages/sales-intelligence/modules/ai-agent/queries/useFeedbackMutation";
import type { FeedbackReaction } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import FeedbackButton from "app/pages/sales-intelligence/modules/ai-agent/components/FeedbackButton/FeedbackButton";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";

import { useQuillEditor } from "../../hooks/useQuillEditor";

import { AIAgentEmailBodyEditorLoader } from "./AIAgentEmailBodyEditorLoader";
import { StyledEditorContainer } from "./styles";

type EditorProps = {
    isLoading?: boolean;
    isInitialLoading?: boolean;
    isUpdatingVersion?: boolean;
    threadId: string;
    messageId: string;
    selectedCompany: AiAgentEmailAutocompleteCompany | null;
    subject: string;
    recipient: Contact | null;
    isCompareChart?: boolean;
    value?: string;
    onTextChange: (value: string, imageIndex: number) => void;
    imgSrc?: string;
    imageIndex?: number;
    companyName?: string;
};

export const AIAgentEmailBodyEditor = ({
    isLoading = false,
    isInitialLoading = false,
    isUpdatingVersion = false,
    value,
    subject,
    recipient,
    onTextChange,
    imgSrc,
    imageIndex,
    threadId,
    messageId,
    selectedCompany,
    isCompareChart = false,
}: EditorProps) => {
    const companyName = selectedCompany?.companyName || selectedCompany?.domain || "";

    const { editorElRef, onCopy } = useQuillEditor({
        value,
        onTextChange,
        imgSrc,
        imageIndex,
        imageClassName: isCompareChart ? "ai-agent-email-image-compare" : undefined,
        companyName,
    });
    const { mutate } = useFeedbackMutation({ isEmail: true });
    const [reaction, setReaction] = useState<FeedbackReaction | undefined>(undefined);

    useEffect(() => {
        setReaction(undefined);
    }, [threadId]);

    const handleSubmitFeedback = (newReaction: FeedbackReaction) => {
        if (reaction === newReaction) {
            return;
        }
        setReaction(newReaction);
        mutate({
            threadId,
            messageId,
            liked: newReaction === "like",
        });
        AIAgentTrackingService.email.trackSubmitFeedback(companyName, newReaction);
    };

    const handleCopy = () => {
        void onCopy({
            subject,
            recipient,
        });
    };

    return (
        <Stack
            flex="1"
            maxHeight="100%"
            gap="16px"
        >
            <Box
                position="relative"
                flex="1"
                maxHeight="100%"
                overflow="hidden"
            >
                {isUpdatingVersion ? <AIAgentEmailBodyEditorLoader /> : null}
                <StyledEditorContainer ref={editorElRef} />
            </Box>

            <Flex
                justifyContent="space-between"
                width="100%"
            >
                <Flex>
                    <FeedbackButton
                        key="like"
                        reaction="like"
                        isActive={reaction === "like"}
                        onFeedback={() => handleSubmitFeedback("like")}
                        isDisabled={isLoading || isUpdatingVersion}
                        dataAutomation="like-button"
                    />

                    <FeedbackButton
                        key="dislike"
                        reaction="dislike"
                        isActive={reaction === "dislike"}
                        onFeedback={() => handleSubmitFeedback("dislike")}
                        isDisabled={isLoading || isUpdatingVersion}
                        dataAutomation="dislike-button"
                    />
                </Flex>
                <IconButton
                    iconName="copy-rounded"
                    onClick={handleCopy}
                    isDisabled={isInitialLoading || isUpdatingVersion}
                    data-automation="copy-button"
                >
                    {translate("si.pages.ai_agent.copy")}
                </IconButton>
            </Flex>
        </Stack>
    );
};
